import type { CallbackInterface } from 'recoil'

//  Id, Name, LastModifiedDate, LastActivationDate

import { errorUpdater, pageState } from 'State/page'

import type { PageStateType } from 'Types/state'

import { logAppError } from 'Helpers/logger'

import type { PaginationState } from 'Pages/UnsubscriptionCampaignsList/types'

import {
  modalActivateSimulateState,
  modalCopyState,
  modalDeleteState,
  modalSimulateState,
} from 'Pages/UnsubscriptionCampaignsList/recoil/state/modals'
import {
  paginationState,
  resultsState,
  utilsState,
} from 'Pages/UnsubscriptionCampaignsList/recoil/state/results'
import {
  fetchCanCreate,
  fetchResults,
  fetchWorkflow,
  notifyToggleStatus,
  setWorkflowStatus,
} from 'Pages/UnsubscriptionCampaignsList/services/results'

export const fetchResultsCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (
    {
      pageNumber,
      pageSize,
      orderBy,
      orderDescending,
    }: {
      pageNumber?: number
      pageSize?: number
      orderBy?: string
      orderDescending?: boolean
    },
    init = false,
  ) => {
    if (init) {
      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        loading: true,
      }))

      const canCreate = await fetchCanCreate()

      set(utilsState, (prev) => ({
        ...prev,
        canCreate,
      }))
    }

    const prevPagination = await snapshot.getPromise(paginationState)

    set(paginationState, (prev) => ({ ...prev, loading: true }))

    const newPagination: PaginationState = {
      ...prevPagination,
      pageNumber:
        pageNumber !== undefined ? pageNumber : prevPagination.pageNumber,
      pageSize: pageSize !== undefined ? pageSize : prevPagination.pageSize,
      orderBy: orderBy !== undefined ? orderBy : prevPagination.orderBy,
      orderDescending:
        orderDescending !== undefined
          ? orderDescending
          : orderBy !== undefined && orderBy === prevPagination.orderBy
            ? !prevPagination.orderDescending
            : prevPagination.orderDescending,
    }

    try {
      //
      const [items, paginationOut] = await fetchResults(newPagination)

      set(resultsState, items)
      set(paginationState, (prev) => ({
        ...prev,
        ...newPagination,
        ...paginationOut,
        loading: false,
      }))

      if (init) {
        set<PageStateType>(pageState, (prev) => ({
          ...prev,
          status: 'ready',
        }))
      }

      //
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'UnsubscriptionCampaignsList',
        Method: 'fetchResultsCallback',
        appError: error,
      })
      set<PageStateType>(pageState, errorUpdater)
    }

    if (init) {
      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        loading: false,
      }))
    }
  }

export const resetPageCallback =
  ({ reset }: CallbackInterface) =>
  async () => {
    reset(paginationState)
    reset(resultsState)
    reset(utilsState)

    reset(modalCopyState)
    reset(modalSimulateState)
    reset(modalDeleteState)
    reset(modalActivateSimulateState)
  }

export const toggleStatusCallback =
  ({ snapshot, set }: CallbackInterface) =>
  async (idWorkflow: number) => {
    const items = await snapshot.getPromise(resultsState)

    const item = items.find((o) => o.Id === idWorkflow)

    if (item !== undefined) {
      if (item.Enabled) {
        set<PageStateType>(pageState, (prev) => ({
          ...prev,
          loading: true,
        }))

        try {
          await setWorkflowStatus(idWorkflow, false)

          set(resultsState, (prev) =>
            prev.map((o) =>
              o.Id === idWorkflow
                ? {
                    ...o,
                    Enabled: false,
                  }
                : o,
            ),
          )

          notifyToggleStatus(false)
        } catch (error) {
          console.error('### error', error)

          logAppError({
            Component: 'UnsubscriptionCampaignsList',
            Method: 'toggleStatusCallback',
            appError: error,
          })
          set<PageStateType>(pageState, errorUpdater)
        }

        set<PageStateType>(pageState, (prev) => ({
          ...prev,
          loading: false,
        }))
      } else {
        try {
          const wf = await fetchWorkflow(idWorkflow)

          set(modalActivateSimulateState, {
            idWorkflow,
            mode: 'activate',
            recipientLimit: wf.RecipientLimit ?? 0,
            show: true,
          })
        } catch (error) {
          console.error('### error', error)

          logAppError({
            Component: 'UnsubscriptionCampaignsList',
            Method: 'toggleStatusCallback',
            appError: error,
          })
          set<PageStateType>(pageState, errorUpdater)
        }
      }
    }
  }

export const activateCallback =
  ({ snapshot, set, reset }: CallbackInterface) =>
  async (idWorkflow: number) => {
    const items = await snapshot.getPromise(resultsState)

    const item = items.find((o) => o.Id === idWorkflow)

    if (item !== undefined) {
      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        loading: true,
      }))

      reset(modalActivateSimulateState)

      try {
        await setWorkflowStatus(idWorkflow, true)

        set(resultsState, (prev) =>
          prev.map((o) =>
            o.Id === idWorkflow
              ? {
                  ...o,
                  Enabled: true,
                }
              : o,
          ),
        )

        notifyToggleStatus(true)
      } catch (error) {
        console.error('### error', error)

        logAppError({
          Component: 'UnsubscriptionCampaignsList',
          Method: 'activateCallback',
          appError: error,
        })
        set<PageStateType>(pageState, errorUpdater)
      }

      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        loading: false,
      }))
    }
  }
